import { useLocation, useNavigate } from "react-router-dom";
import { BreadCrumb } from "primereact/breadcrumb";
import MenuProfile from "../MenuProfile";
import { useFunctions } from "presentation/hooks/use-query-functions";
import { BaseInputFilter } from "domain/models/base-input-filter";
import { translateText } from "infra/translation/translate";
import { useImpersonationStore } from "presentation/stores/use-impersonation-store";
import { useAuthStore } from "presentation/stores/use-auth-store";
import { Button } from "primereact/button";
import "./index.css";

interface FunctionHeaderProps {
  title?: string;
  subtitle?: string;
  breadcrumbs?: any[];
}

const actionMap: Record<string, string> = {
  new: "Novo",
  edit: "Editar",
};

const FunctionHeader = ({ title, subtitle, breadcrumbs }: FunctionHeaderProps) => {
  const location = useLocation();
  const navigate = useNavigate();

  const { isDebugMode, originalEmployee, disableImpersonation } = useImpersonationStore();
  const { userEmployee } = useAuthStore();

  const handleDisableDebug = () => {
    disableImpersonation();
    window.location.reload();
  };

  const { data: functions } = useFunctions({
    limit: 100,
    sortBy: "order",
    sortOrder: "asc",
  } as BaseInputFilter);

  const generateBreadcrumbs = () => {
    if (breadcrumbs) return breadcrumbs;
    
    const paths = location.pathname.split("/").filter((p) => p);
    if (paths.length === 0) return [];

    const functionNameStr = paths[0]; 
    const actionStr = paths.length > 1 ? paths[1] : null;

    let currentUrl = `/${functionNameStr}`;
    const fn = functions?.data?.find((f: any) => f.name === functionNameStr);
    const items = [];
    
    if (fn) {
      if (fn.category) {
        // Categoria (sem clique, apenas visual)
        items.push({ label: fn.category, command: () => {} });
      }
      items.push({ 
        label: translateText(fn.label, "pt") || fn.label, 
        command: () => navigate(`/${functionNameStr}`) 
      });
    } else {
      // Fallback caso não ache a função
      let label = functionNameStr.charAt(0).toUpperCase() + functionNameStr.slice(1);
      if (functionNameStr === 'dashboards') label = 'Dashboard';
      items.push({
        label,
        command: () => navigate(`/${functionNameStr}`),
      });
    }

    if (actionStr) {
      currentUrl += `/${actionStr}`;
      const isId = actionStr.length > 20 || !isNaN(Number(actionStr));
      
      if (!isId) {
         items.push({
           label: actionMap[actionStr] || actionStr.charAt(0).toUpperCase() + actionStr.slice(1),
           command: () => navigate(currentUrl),
         });
      } else {
         items.push({
           label: "Detalhes",
           command: () => navigate(currentUrl),
         });
      }
    }
    
    if (paths.length > 2) {
       const extraStr = paths[2];
       currentUrl += `/${extraStr}`;
       const isExtraId = extraStr.length > 20 || !isNaN(Number(extraStr));
       items.push({
         label: isExtraId ? "Detalhes" : actionMap[extraStr] || extraStr,
         command: () => navigate(currentUrl),
       });
    }

    return items;
  };

  const items = generateBreadcrumbs();
  const home = { icon: "pi pi-home", command: () => navigate("/") };

  return (
    <header className="">
      <div className="page-header cols">
        <div className="col-8">
          {items.length > 0 && (
            <div className="breadcrumb-wrapper">
              <BreadCrumb model={items} home={home} />
            </div>
          )}
          <div className="title ">{title}</div>
          <div className="subtitle ">{subtitle}</div>
        </div>

        {isDebugMode && (
          <div className="col-2" style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', paddingRight: '1rem' }}>
            <div style={{
              backgroundColor: '#ff9800',
              color: 'white',
              padding: '6px 12px',
              display: 'flex',
              alignItems: 'center',
              fontWeight: 'bold',
              borderRadius: '8px',
              fontSize: '12px',
              gap: '8px',
              boxShadow: '0 2px 4px rgba(0,0,0,0.2)'
            }}>
              <span>A simular: <strong>{userEmployee?.name?.split(' ')[0]}</strong></span>
              <Button icon="pi pi-power-off" onClick={handleDisableDebug} className="p-button-rounded p-button-danger p-button-sm p-button-text" style={{ padding: '0.2rem', color: '#fff', width: '24px', height: '24px' }} title={`Desligar Simulação (Original: ${originalEmployee?.name || 'Developer'})`} />
            </div>
          </div>
        )}

        <div className="profile col-2">
          <MenuProfile />
        </div>
      </div>
    </header>
  );
};

export default FunctionHeader;
